import { Directive, effect, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';
import { ModuleKey } from '@cosmos/shared';
import { EntitlementService } from '@cosmos/angular/utils';

/**
 * Structural directive that hides its host when the module is not visible
 * (status='disabled'). Also hides when mode='preview' as a safety fallback
 * (ModuleAccessGuard handles preview at the route level, so this should
 * rarely trigger, but guards against stale state).
 *
 * Operates at MODULE level — controls visibility of entire sidebar sections
 * and page regions. Does NOT handle element-level intent (locked state, quota,
 * feature gates) — use [cosmosAction] for that.
 *
 * Usage:
 *   <div *cosmosModuleAccess="'monitoring'">...</div>
 */
@Directive({
  selector: '[cosmosModuleAccess]',
  standalone: true,
})
export class ModuleAccessDirective {
  private readonly entitlementService = inject(EntitlementService);
  private readonly vcr = inject(ViewContainerRef);
  private readonly tmpl = inject(TemplateRef<unknown>);

  readonly cosmosModuleAccess = input.required<ModuleKey>();

  constructor() {
    effect(() => {
      const module = this.cosmosModuleAccess();
      const access = this.entitlementService.moduleAccess()[module];

      const visible = access?.visible ?? false;
      if (!visible) {
        this.vcr.clear();
        return;
      }

      if (this.vcr.length === 0) {
        this.vcr.createEmbeddedView(this.tmpl);
      }
    });
  }
}

/** @deprecated Use ModuleAccessDirective. Will be removed after full migration. */
export { ModuleAccessDirective as EntitlementDirective };
